import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Image } from 'primereact/image';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useAuth } from '../../hooks/useAuth';
import { clientsApi } from '../../api/clients';
import { authApi } from '../../api/auth';
import { Client, TwoFactorStatusResponse } from '../../types';
import ClientSettings from '../../screens/ClientSettings';
import { JobIndicator } from './JobIndicator';

export const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams<{ clientId: string }>();
  const userMenu = useRef<OverlayPanel>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [showClientSettings, setShowClientSettings] = useState(false);
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatusResponse | null>(null);

  // Calculate grace period info
  const gracePeriodInfo = useMemo(() => {
    if (!user?.totp_grace_period_ends_at || twoFactorStatus?.enabled) {
      return null;
    }
    const endDate = new Date(user.totp_grace_period_ends_at);
    const now = new Date();
    const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysRemaining <= 0) {
      return { expired: true, days: 0 };
    }
    return { expired: false, days: daysRemaining };
  }, [user?.totp_grace_period_ends_at, twoFactorStatus?.enabled]);

  // Extract clientId from URL path since useParams may not work outside Route context
  const getClientIdFromPath = (): string | null => {
    const match = location.pathname.match(/\/clients\/([^/]+)/);
    return match ? match[1] : null;
  };

  const currentClientId = clientId || getClientIdFromPath();

  useEffect(() => {
    const fetchClient = async () => {
      if (!currentClientId) {
        setClient(null);
        return;
      }

      try {
        const clientData = await clientsApi.getClient(currentClientId);
        setClient(clientData);
      } catch (error) {
        console.error('Failed to fetch client:', error);
        setClient(null);
      }
    };

    fetchClient();
  }, [currentClientId]);

  // Load 2FA status
  useEffect(() => {
    const load2FAStatus = async () => {
      try {
        const status = await authApi.get2FAStatus();
        setTwoFactorStatus(status);
      } catch {
        // Status will remain null
      }
    };
    if (user) {
      load2FAStatus();
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleClientSettingsClick = () => {
    if (currentClientId) {
      setShowClientSettings(true);
    }
  };

  const handleClientUpdated = (updatedClient: Client) => {
    setClient(updatedClient);
  };

  const handleMenuItemClick = (action: () => void) => {
    userMenu.current?.hide();
    action();
  };

  return (
    <>
      <header>
        <Image
          src={user?.org_logo_url || "/tallai-logo.png"}
          alt="Logo"
          className="org-logo"
          onError={(e) => { (e.target as HTMLImageElement).src = "/logo.png"; }}
          onClick={handleLogoClick}
        />
        {/* <OrgSwitcher /> */}
        {client && (
          <div className="client-info" onClick={handleClientSettingsClick}>
            <h2>{client.name}</h2>
            <Button
              className={client.qbo_realm_id ? 'qb-icon connected' : 'qb-icon disconnected'}
              tooltip={client.qbo_realm_id ? 'QuickBooks Connected' : 'QuickBooks Not Connected'}
              tooltipOptions={{ position: 'bottom' }}
            >
              <svg width="100%" height="100%" viewBox="0 0 323 270" 
              version="1.1" xmlns="http://www.w3.org/2000/svg">
                  <g transform="matrix(1,0,0,1,-5.426814,-28.487672)">
                      <g transform="matrix(1,0,0,1,-121,-117)">
                          <path d="M328.5,370.966C298.779,370.966 297.874,371.305 297.542,369.492C297.455,369.017 297.491,148.231 297.526,147.503C297.639,145.105 298.959,145.305 327.486,145.801C330.34,145.851 329.802,147.459 329.802,177.5C329.802,337.504 329.711,337.757 330.53,338.454C330.866,338.74 330.938,338.828 356.5,338.798C429.039,338.71 437.651,237.701 370.554,222.264C362.191,220.34 350.644,223.357 350.215,219.533C350.183,219.247 350.202,191.538 350.203,191.48C350.256,189.696 350.749,187.518 372.448,189.861C409.432,193.855 462.587,236.487 445.631,304.536C437.743,336.194 413.898,352.176 409.789,354.931C382.433,373.266 362.677,370.966 328.5,370.966Z"/>
                      </g>
                      <g transform="matrix(1,0,0,1,-121,-117)">
                          <path d="M220.5,339.034C223.887,339.324 224.975,338.24 225.174,340.546C225.21,340.962 225.215,369.043 225.174,369.454C224.949,371.697 223.792,370.616 208.503,371.033C177.8,371.869 110.657,331.42 129.805,255.58C137.724,224.211 168.377,189.662 217.5,189.497C222.218,189.482 275.269,189.304 276.47,189.605C277.982,189.984 277.802,190.767 277.802,282.5C277.802,413.27 277.884,413.514 277.152,414.082C276.711,414.425 246.548,415.514 245.691,413.422C244.886,411.459 246.375,223.574 244.902,222.098C244.867,222.062 214.472,218.845 197.398,225.229C137.963,247.452 151.422,330.306 208.479,338.674C209.431,338.814 210.159,338.921 220.5,339.034Z"/>
                      </g>
                  </g>
              </svg>
            </Button>
          </div>
        )}
        <div className="header-actions">
          {gracePeriodInfo && !gracePeriodInfo.expired && (
            <Button
              icon="ms-out ms-out-warning"
              onClick={() => navigate('/profile?setup2fa=true')}
              label={`2FA Required (${gracePeriodInfo.days}d)`}
              severity="warning"
              size="small"
              tooltip={`Set up two-factor authentication. ${gracePeriodInfo.days} days remaining.`}
              tooltipOptions={{ position: 'left' }}
            />
          )}
          {user?.is_superuser && (
            <Button
              icon="ms-out ms-out-admin-panel-settings"
              onClick={() => navigate('/admin')}
              label="Admin"
              link
              tooltip="Admin"
              tooltipOptions={{ position: 'left' }}
            />
          )}
          <JobIndicator clientId={currentClientId} />
          <Button
            icon="ms-out ms-out-person"
            onClick={(e) => userMenu.current?.toggle(e)}
            className="user-settings"
            aria-label="User settings"
            tooltip={user?.full_name || user?.email || 'User'}
            tooltipOptions={{ position: 'left' }}
          />
          <OverlayPanel ref={userMenu} className="user-menu-panel">
            <div className="user-menu-header">{user?.full_name || user?.email || 'User'}</div>
            <Button
              label="Profile"
              icon="ms-out ms-out-person"
              link
              className="user-menu-item"
              onClick={() => handleMenuItemClick(() => navigate('/profile'))}
            />
            <Button
              label="Job History"
              icon="ms-out ms-out-history"
              link
              className="user-menu-item"
              onClick={() => handleMenuItemClick(() => navigate('/job-history'))}
            />
            <hr />
            <Button
              label="Logout"
              icon="ms-out ms-out-logout"
              link
              className="user-menu-item"
              onClick={() => handleMenuItemClick(handleLogout)}
            />
          </OverlayPanel>
        </div>
      </header>

      {currentClientId && (
        <ClientSettings
          clientId={currentClientId}
          visible={showClientSettings}
          onHide={() => setShowClientSettings(false)}
          onClientUpdated={handleClientUpdated}
        />
      )}
    </>
  );
};
